import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Calendar, Clock, Users, ArrowLeft, Save, BookOpen } from 'lucide-react';

const CreateClassPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    classTypeId: '',
    day: 'Monday',
    time: '9:00 AM',
    totalSpots: 5
  });
  
  // UI state
  const [classTypes, setClassTypes] = useState([]);
  const [selectedClassType, setSelectedClassType] = useState(null);
  const [loadingClassTypes, setLoadingClassTypes] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Options for form fields
  const days = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  const timeSlots = [
    '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
    '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM'
  ];

  // Load class types on mount
  useEffect(() => {
    const loadClassTypes = async () => {
      try {
        setLoadingClassTypes(true);
        const types = await api.classTypes.getActive();
        setClassTypes(types);
        
        // Select first class type by default
        if (types.length > 0) {
          setFormData(prev => ({ ...prev, classTypeId: types[0].$id }));
          setSelectedClassType(types[0]);
        }
      } catch (err) {
        console.error('Error loading class types:', err);
        setError('Failed to load class types');
      } finally {
        setLoadingClassTypes(false);
      }
    };

    loadClassTypes();
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update selected class type when classTypeId changes
    if (name === 'classTypeId') {
      const selected = classTypes.find(ct => ct.$id === value);
      setSelectedClassType(selected);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Validate form
      if (!formData.classTypeId || !formData.day || !formData.time || !formData.totalSpots) {
        setError('Please fill in all fields');
        return;
      }

      if (formData.totalSpots < 1 || formData.totalSpots > 20) {
        setError('Total spots must be between 1 and 20');
        return;
      }
      
      // Call API to create class
      const response = await api.classes.create({
        classTypeId: formData.classTypeId,
        classType: selectedClassType?.name || 'general', // For backward compatibility
        day: formData.day,
        time: formData.time,
        totalSpots: parseInt(formData.totalSpots)
      });
      
      if (response.success) {
        setSuccess(true);
        // Redirect to admin dashboard after a short delay
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to create class');
      }
    } catch (err) {
      console.error('Error creating class:', err);
      setError(err.message || 'Failed to create class. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle back button
  const handleBack = () => {
    navigate('/admin');
  };

  // Show success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
        <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
          <div className="container mx-auto p-4 flex justify-between items-center">
            <h1 className="text-xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
                Admin Dashboard
              </span>
            </h1>
          </div>
          <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
        </header>

        <main className="container mx-auto py-8 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="inline-block text-6xl font-light text-green-400 opacity-20 mb-4">
              ✓
            </div>
            <h2 className="text-2xl font-bold mb-4 text-green-400">Class Created Successfully!</h2>
            <p className="text-gray-300 mb-6">
              The new "{selectedClassType?.name}" class for {formData.day} at {formData.time} has been created.
            </p>
            <div className="animate-pulse text-purple-400">
              Redirecting to admin dashboard...
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Admin Dashboard
            </span>
          </h1>
          
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 text-sm hidden sm:inline">
              Admin: {user?.name}
            </span>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Back button */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-gray-400 hover:text-white flex items-center"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Dashboard
            </Button>
          </div>

          {/* Page title */}
          <div className="text-center mb-8">
            <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
              新课程
            </div>
            <h2 className="text-2xl font-bold">Create New Class</h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Loading state for class types */}
          {loadingClassTypes ? (
            <div className="text-center py-8">
              <svg className="animate-spin h-8 w-8 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-2 text-gray-400">Loading class types...</p>
            </div>
          ) : classTypes.length === 0 ? (
            <div className="text-center py-8 bg-gray-800 rounded-lg border border-gray-700">
              <p className="text-gray-400 mb-4">No class types available</p>
              <p className="text-gray-500 text-sm mb-4">You need to create class types before you can create classes.</p>
              <Button
                variant="primary"
                onClick={() => navigate('/admin')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Go to Settings → Class Types
              </Button>
            </div>
          ) : (
            <>
              {/* Create class form */}
              <form onSubmit={handleSubmit}>
                <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
                    <h3 className="font-semibold text-lg flex items-center">
                      <Calendar size={20} className="mr-2" />
                      Class Details
                    </h3>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Class Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <BookOpen size={16} className="inline mr-2" />
                        Class Type *
                      </label>
                      <select
                        name="classTypeId"
                        value={formData.classTypeId}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                        required
                      >
                        <option value="" className="bg-gray-800">Select a class type...</option>
                        {classTypes.map(classType => (
                          <option key={classType.$id} value={classType.$id} className="bg-gray-800">
                            {classType.name}
                          </option>
                        ))}
                      </select>
                      {selectedClassType && selectedClassType.description && (
                        <p className="text-gray-400 text-sm mt-2">
                          {selectedClassType.description}
                        </p>
                      )}
                    </div>

                    {/* Day */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <Calendar size={16} className="inline mr-2" />
                        Day of Week *
                      </label>
                      <select
                        name="day"
                        value={formData.day}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                        required
                      >
                        {days.map(day => (
                          <option key={day} value={day} className="bg-gray-800">
                            {day}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <Clock size={16} className="inline mr-2" />
                        Time *
                      </label>
                      <select
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                        required
                      >
                        {timeSlots.map(time => (
                          <option key={time} value={time} className="bg-gray-800">
                            {time}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Total Spots */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <Users size={16} className="inline mr-2" />
                        Maximum Students *
                      </label>
                      <input
                        type="number"
                        name="totalSpots"
                        value={formData.totalSpots}
                        onChange={handleChange}
                        min="1"
                        max="20"
                        className="w-full p-3 border border-gray-600 bg-[rgba(255,255,255,0.05)] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                        placeholder="Enter number of spots (1-20)"
                        required
                      />
                      <p className="text-gray-400 text-sm mt-1">
                        Recommended: 3-8 students for optimal learning
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form actions */}
                <div className="flex justify-between mt-8">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleBack}
                    className="bg-gray-700 hover:bg-gray-600 border border-gray-600"
                  >
                    Cancel
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                    disabled={!selectedClassType}
                    className="bg-purple-600 hover:bg-purple-700 flex items-center"
                  >
                    <Save size={16} className="mr-2" />
                    Create Class
                  </Button>
                </div>
              </form>

              {/* Preview card */}
              {selectedClassType && (
                <div className="mt-8 bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-6 py-3 border-b border-gray-700">
                    <h3 className="font-semibold text-lg">Class Preview</h3>
                  </div>
                  
                  <div className="p-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-400 text-sm">Class Type</p>
                        <p className="font-medium text-purple-300">
                          {selectedClassType.name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Schedule</p>
                        <p className="font-medium text-purple-300">
                          {formData.day} at {formData.time}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Capacity</p>
                        <p className="font-medium text-purple-300">
                          {formData.totalSpots} students
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Status</p>
                        <p className="font-medium text-green-400">
                          Active (0/{formData.totalSpots} enrolled)
                        </p>
                      </div>
                    </div>
                    {selectedClassType.category && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <p className="text-gray-400 text-sm">Category</p>
                        <p className="font-medium text-purple-300">
                          {selectedClassType.category}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default CreateClassPage;